import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChatTeardropDotsIcon,
  HouseIcon,
} from '@phosphor-icons/react';

import config from '@plone/volto/registry';
import { UniversalLink } from '@plone/volto/components';
import { Helmet, BodyClass } from '@plone/volto/helpers';
import { getNavigation } from '@plone/volto/actions';
import {
  toBackendLang,
  withServerErrorCode,
} from '@plone/volto/helpers/Utils/Utils';

import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import { useCanGoBack } from 'volto-ministerio-publico-federal/hooks';

import NotFoundFeedbackModal from './NotFoundFeedbackModal';
import NotFoundIllustration from './NotFoundIllustration';
import { NOT_FOUND_COPY } from './constants';

/**
 * Página 404 do portal (Figma 5376:37833 desktop, 5463:65389 mobile).
 *
 * Mobile-first: a base é a coluna centralizada do mobile e os prefixos `sm:`
 * promovem para o layout de duas colunas do desktop.
 */
export const NotFound: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const lang = useSelector((state: any) => state.intl.locale);
  const canGoBack = useCanGoBack();
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);

  // Herdado do NotFound do core: a navegação global pode não ter sido
  // carregada (o erro pode ser a primeira resposta da sessão), e o cabeçalho
  // depende dela.
  React.useEffect(() => {
    dispatch(
      getNavigation(
        config.settings.isMultilingual ? `/${toBackendLang(lang)}` : '/',
        config.settings.navDepth,
      ),
    );
  }, [dispatch, lang]);

  return (
    <>
      <Helmet title={NOT_FOUND_COPY.title} />
      <BodyClass className="page-not-found" />

      <section className="mpf-not-found">
        <div className="mpf-not-found__hero">
          <NotFoundIllustration className="mpf-not-found__illustration" />

          <div className="flex flex-col gap-4">
            <h1 className="mb-0 text-h2 font-semibold text-primary dark:text-night-100 sm:text-h1 sm:font-bold">
              {NOT_FOUND_COPY.title}
            </h1>
            <p className="mb-0 text-lg text-on-surface sm:text-reading">
              {NOT_FOUND_COPY.lead}
            </p>
            <p className="mb-0 text-base text-on-surface-low">
              {NOT_FOUND_COPY.support}
            </p>
          </div>
        </div>

        <hr className="mpf-not-found__rule" />

        <nav className="mpf-not-found__ctas" aria-label="O que fazer agora">
          {canGoBack && (
            <MPFButton
              variant="ghost"
              className="mpf-not-found__cta"
              onClick={() => window.history.back()}
            >
              <ArrowLeftIcon size={24} aria-hidden="true" />
              {NOT_FOUND_COPY.back}
            </MPFButton>
          )}

          <MPFButton
            as={UniversalLink}
            href="/"
            variant="ghost"
            className="mpf-not-found__cta"
          >
            <HouseIcon size={24} aria-hidden="true" />
            {NOT_FOUND_COPY.home}
          </MPFButton>

          <MPFButton
            variant="ghost"
            className="mpf-not-found__cta"
            aria-haspopup="dialog"
            aria-expanded={isFeedbackOpen}
            onClick={() => setIsFeedbackOpen(true)}
          >
            <ChatTeardropDotsIcon size={24} aria-hidden="true" />
            {NOT_FOUND_COPY.feedback}
          </MPFButton>
        </nav>
      </section>

      <NotFoundFeedbackModal
        open={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        pagePath={location.pathname}
      />
    </>
  );
};

export default withServerErrorCode(404)(NotFound);
